'use client';

import { useEffect, useState } from 'react';

type CharacterState = 'idle' | 'watching' | 'hiding' | 'success' | 'error';

interface Props {
  state: CharacterState;
  emailLength?: number;
}

function useMouseSvg(containerRef: HTMLDivElement | null, viewW: number, viewH: number) {
  const [pos, setPos] = useState({ x: viewW / 2, y: viewH / 2 });
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!containerRef) return;
      const rect = containerRef.getBoundingClientRect();
      setPos({
        x: ((e.clientX - rect.left) / rect.width) * viewW,
        y: ((e.clientY - rect.top) / rect.height) * viewH,
      });
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [containerRef, viewW, viewH]);
  return pos;
}

function eyeDir(
  creatureCx: number, creatureCy: number,
  mouseX: number, mouseY: number,
  state: CharacterState, emailLength: number, max: number
) {
  if (state === 'watching') {
    return { x: Math.min(emailLength * 0.5, max) - max / 2, y: 2 };
  }
  if (state === 'hiding') {
    return { x: max, y: -2 };
  }
  // Follow mouse
  const dx = mouseX - creatureCx;
  const dy = mouseY - creatureCy;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  return {
    x: (dx / dist) * Math.min(dist * 0.04, max),
    y: (dy / dist) * Math.min(dist * 0.04, max * 0.7),
  };
}

export default function LoginCharacter({ state, emailLength = 0 }: Props) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [blinks, setBlinks] = useState([false, false, false, false]);
  const mouse = useMouseSvg(container, 350, 280);

  // Staggered blinking
  useEffect(() => {
    const timers = [0, 1, 2, 3].map(i =>
      setInterval(() => {
        setBlinks(prev => { const n = [...prev]; n[i] = true; return n; });
        setTimeout(() => setBlinks(prev => { const n = [...prev]; n[i] = false; return n; }), 140);
      }, 2500 + i * 800 + Math.random() * 1500)
    );
    return () => timers.forEach(clearInterval);
  }, []);

  const baseY = 220; // shared baseline

  // Eye helper
  const Eye = ({ cx, cy, r, pupilR, blink: b, ex, ey }: {
    cx: number; cy: number; r: number; pupilR: number;
    blink: boolean; ex: number; ey: number;
  }) => (
    <g style={{ transition: 'transform 0.15s ease-out', transform: `translate(${ex * 0.15}px, ${ey * 0.15}px)` }}>
      <ellipse cx={cx} cy={cy} rx={r} ry={b ? 1.5 : r} fill="white" style={{ transition: 'ry 0.08s' }} />
      {!b && (
        <>
          <circle cx={cx + ex * 0.35} cy={cy + ey * 0.35} r={pupilR} fill="#111" />
          <circle cx={cx + ex * 0.35 - 1.2} cy={cy + ey * 0.35 - 1.2} r={pupilR * 0.3} fill="white" />
        </>
      )}
    </g>
  );

  // 1. PURPLE tall rectangle (backmost, left)
  const purpleX = 110, purpleW = 75, purpleH = 170;
  const purpleTop = baseY - purpleH;
  const pe = eyeDir(purpleX, purpleTop + 50, mouse.x, mouse.y, state, emailLength, 6);

  // 2. BLACK tall rectangle (center)
  const blackX = 160, blackW = 65, blackH = 130;
  const blackTop = baseY - blackH;
  const be = eyeDir(blackX, blackTop + 40, mouse.x, mouse.y, state, emailLength, 6);

  // 3. ORANGE semicircle (front-left, widest)
  const orangeX = 115, orangeW = 110, orangeH = 80;
  const orangeTop = baseY - orangeH;
  const oe = eyeDir(orangeX, orangeTop + 25, mouse.x, mouse.y, state, emailLength, 5);

  // 4. YELLOW dome (front-right)
  const yellowX = 220, yellowW = 80, yellowH = 100;
  const yellowTop = baseY - yellowH;
  const ye = eyeDir(yellowX, yellowTop + 35, mouse.x, mouse.y, state, emailLength, 5);

  return (
    <div ref={setContainer} className="flex justify-center select-none">
      <svg width="350" height="280" viewBox="0 0 350 280">
        {/* BG shadow */}
        <ellipse cx="165" cy={baseY + 5} rx="130" ry="12" fill="rgba(0,0,0,0.04)" />

        <style>{`
          @keyframes dropIn1 {
            0% { transform: translate(${purpleX - 200}px, -300px) rotate(-40deg) scale(0.5); opacity: 0; }
            30% { opacity: 1; }
            60% { transform: translate(0px, 15px) rotate(5deg) scale(1.05); }
            75% { transform: translate(0px, -8px) rotate(-2deg) scale(0.98); }
            90% { transform: translate(0px, 3px) rotate(0.5deg) scale(1.01); }
            100% { transform: translate(0px, 0px) rotate(0deg) scale(1); }
          }
          @keyframes dropIn2 {
            0% { transform: translate(0px, -350px) rotate(30deg) scale(0.4); opacity: 0; }
            30% { opacity: 1; }
            60% { transform: translate(0px, 18px) rotate(-6deg) scale(1.06); }
            75% { transform: translate(0px, -10px) rotate(3deg) scale(0.97); }
            90% { transform: translate(0px, 4px) rotate(-1deg) scale(1.01); }
            100% { transform: translate(0px, 0px) rotate(0deg) scale(1); }
          }
          @keyframes dropIn3 {
            0% { transform: translate(${-(orangeX - 50)}px, -250px) rotate(50deg) scale(0.6); opacity: 0; }
            30% { opacity: 1; }
            60% { transform: translate(0px, 12px) rotate(-4deg) scale(1.04); }
            75% { transform: translate(0px, -6px) rotate(2deg) scale(0.98); }
            90% { transform: translate(0px, 2px) rotate(-0.5deg) scale(1); }
            100% { transform: translate(0px, 0px) rotate(0deg) scale(1); }
          }
          @keyframes dropIn4 {
            0% { transform: translate(${350 - yellowX + 100}px, -280px) rotate(-35deg) scale(0.5); opacity: 0; }
            30% { opacity: 1; }
            60% { transform: translate(0px, 14px) rotate(4deg) scale(1.05); }
            75% { transform: translate(0px, -7px) rotate(-2deg) scale(0.98); }
            90% { transform: translate(0px, 3px) rotate(0.5deg) scale(1.01); }
            100% { transform: translate(0px, 0px) rotate(0deg) scale(1); }
          }
          @keyframes sway1 { 0%,100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-3px) rotate(-1deg); } }
          @keyframes sway2 { 0%,100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-4px) rotate(1deg); } }
          @keyframes sway3 { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-2.5px); } }
          @keyframes sway4 { 0%,100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-3.5px) rotate(-0.5deg); } }
          @keyframes peekHide {
            0% { transform: translateX(0); }
            15% { transform: translateX(8px) rotate(3deg); }
            30% { transform: translateX(0) rotate(-1deg); }
            50% { transform: translateX(5px) rotate(2deg); }
            70% { transform: translateX(0); }
            100% { transform: translateX(0); }
          }
          .creature-purple {
            animation: dropIn1 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both,
                       sway1 3s ease-in-out 1.2s infinite;
            transform-origin: ${purpleX}px ${baseY}px;
          }
          .creature-purple.hiding {
            animation: peekHide 1.2s ease-in-out both;
            transform-origin: ${purpleX}px ${baseY}px;
          }
          .creature-black {
            animation: dropIn2 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both,
                       sway2 3.5s ease-in-out 1.4s infinite;
            transform-origin: ${blackX}px ${baseY}px;
          }
          .creature-black.hiding {
            animation: peekHide 1.2s ease-in-out 0.08s both;
            transform-origin: ${blackX}px ${baseY}px;
          }
          .creature-orange {
            animation: dropIn3 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both,
                       sway3 2.8s ease-in-out 1.5s infinite;
            transform-origin: ${orangeX}px ${baseY}px;
          }
          .creature-orange.hiding {
            animation: peekHide 1.2s ease-in-out 0.15s both;
            transform-origin: ${orangeX}px ${baseY}px;
          }
          .creature-yellow {
            animation: dropIn4 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both,
                       sway4 3.2s ease-in-out 1.6s infinite;
            transform-origin: ${yellowX}px ${baseY}px;
          }
          .creature-yellow.hiding {
            animation: peekHide 1.2s ease-in-out 0.22s both;
            transform-origin: ${yellowX}px ${baseY}px;
          }
        `}</style>

        {/* 1. PURPLE — tall rounded rect, backmost */}
        <g className={`creature-purple${state === 'hiding' ? ' hiding' : ''}`}>
        <rect
          x={purpleX - purpleW / 2} y={purpleTop}
          width={purpleW} height={purpleH}
          rx={18} fill="#5B2EE5"
        />
        {/* Purple ears/nubs */}
        <circle cx={purpleX - purpleW / 2 + 8} cy={purpleTop + 2} r={8} fill="#5B2EE5" />
        <circle cx={purpleX + purpleW / 2 - 8} cy={purpleTop + 2} r={8} fill="#5B2EE5" />
        {/* Purple eyes */}
        <Eye cx={purpleX - 12} cy={purpleTop + 50} r={5} pupilR={5} blink={blinks[0]} ex={pe.x} ey={pe.y} />
        <Eye cx={purpleX + 12} cy={purpleTop + 50} r={5} pupilR={5} blink={blinks[0]} ex={pe.x} ey={pe.y} />
        {/* Purple mouth */}
        {state === 'success' ? (
          <path d={`M${purpleX - 8},${purpleTop + 68} Q${purpleX},${purpleTop + 78} ${purpleX + 8},${purpleTop + 68}`} fill="none" stroke="#3B1A9E" strokeWidth="2" strokeLinecap="round" />
        ) : state === 'error' ? (
          <line x1={purpleX - 6} y1={purpleTop + 72} x2={purpleX + 6} y2={purpleTop + 72} stroke="#3B1A9E" strokeWidth="2" strokeLinecap="round" />
        ) : (
          <path d={`M${purpleX - 6},${purpleTop + 70} Q${purpleX},${purpleTop + 75} ${purpleX + 6},${purpleTop + 70}`} fill="none" stroke="#3B1A9E" strokeWidth="2" strokeLinecap="round" />
        )}

        </g>

        {/* 2. BLACK — tall rounded rect */}
        <g className={`creature-black${state === 'hiding' ? ' hiding' : ''}`}>
        <rect
          x={blackX - blackW / 2} y={blackTop}
          width={blackW} height={blackH}
          rx={12} fill="#1A1A1A"
        />
        {/* Black eyes (white on dark) */}
        <Eye cx={blackX - 10} cy={blackTop + 40} r={5.5} pupilR={3.5} blink={blinks[1]} ex={be.x} ey={be.y} />
        <Eye cx={blackX + 10} cy={blackTop + 40} r={5.5} pupilR={3.5} blink={blinks[1]} ex={be.x} ey={be.y} />
        {/* Black mouth */}
        {state === 'hiding' && (
          <line x1={blackX + 20} y1={blackTop + 55} x2={blackX + 35} y2={blackTop + 50} stroke="#1A1A1A" strokeWidth="3" strokeLinecap="round" />
        )}
        {state === 'success' ? (
          <path d={`M${blackX - 7},${blackTop + 56} Q${blackX},${blackTop + 64} ${blackX + 7},${blackTop + 56}`} fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" />
        ) : (
          <line x1={blackX - 6} y1={blackTop + 58} x2={blackX + 6} y2={blackTop + 58} stroke="#444" strokeWidth="2" strokeLinecap="round" />
        )}

        </g>

        {/* 3. ORANGE — semicircle/dome, front-left */}
        <g className={`creature-orange${state === 'hiding' ? ' hiding' : ''}`}>
        <ellipse
          cx={orangeX} cy={baseY}
          rx={orangeW / 2} ry={orangeH}
          fill="#E8612D"
          clipPath="inset(0 0 50% 0)"
        />
        <path
          d={`M${orangeX - orangeW / 2},${baseY} A${orangeW / 2},${orangeH} 0 0,1 ${orangeX + orangeW / 2},${baseY} Z`}
          fill="#E8612D"
        />
        {/* Orange eyes */}
        <Eye cx={orangeX - 12} cy={orangeTop + 30} r={5} pupilR={5} blink={blinks[2]} ex={oe.x} ey={oe.y} />
        <Eye cx={orangeX + 12} cy={orangeTop + 30} r={5} pupilR={5} blink={blinks[2]} ex={oe.x} ey={oe.y} />
        {/* Orange mouth */}
        {state === 'success' ? (
          <path d={`M${orangeX - 7},${orangeTop + 48} Q${orangeX},${orangeTop + 58} ${orangeX + 7},${orangeTop + 48}`} fill="#B5421A" />
        ) : state === 'watching' ? (
          <ellipse cx={orangeX} cy={orangeTop + 50} rx={4} ry={3.5} fill="#111" />
        ) : (
          <path d={`M${orangeX - 6},${orangeTop + 48} Q${orangeX},${orangeTop + 54} ${orangeX + 6},${orangeTop + 48}`} fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" />
        )}

        </g>

        {/* 4. YELLOW — taller dome, front-right */}
        <g className={`creature-yellow${state === 'hiding' ? ' hiding' : ''}`}>
        <path
          d={`M${yellowX - yellowW / 2},${baseY} Q${yellowX - yellowW / 2},${yellowTop} ${yellowX},${yellowTop} Q${yellowX + yellowW / 2},${yellowTop} ${yellowX + yellowW / 2},${baseY} Z`}
          fill="#F5C518"
        />
        {/* Yellow eyes */}
        <Eye cx={yellowX - 10} cy={yellowTop + 40} r={4.5} pupilR={4.5} blink={blinks[3]} ex={ye.x} ey={ye.y} />
        <Eye cx={yellowX + 10} cy={yellowTop + 40} r={4.5} pupilR={4.5} blink={blinks[3]} ex={ye.x} ey={ye.y} />
        {/* Yellow beak/mouth — small line to the right */}
        <line
          x1={yellowX + 8} y1={yellowTop + 55}
          x2={yellowX + 22} y2={yellowTop + 54}
          stroke="#111" strokeWidth="2" strokeLinecap="round"
        />
        {state === 'success' && (
          <path d={`M${yellowX - 5},${yellowTop + 56} Q${yellowX},${yellowTop + 63} ${yellowX + 5},${yellowTop + 56}`} fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" />
        )}
        </g>
      </svg>
    </div>
  );
}
